import { formatDateTime, parseUtcTimestamp } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import React, { useState, useEffect, useRef } from 'react';
import { tenantApi, type UserPresenceDto } from '../services/tenantApi';
import { useRealtimeRefresh } from '../shared/useRealtimeRefresh';
import { ONCALL_PRESENCE_CHANGED_EVENT, PRESENCE_POLL_MS } from '../shared/onCallPresenceEvents';
import { PresenceDot } from './PresenceDot';
import './OnCallWidget.css';

const BOUNDARY_GRACE_MS = 2_000;
const MIN_REFRESH_DELAY_MS = 5_000;
const MAX_REFRESH_DELAY_MS = 6 * 60 * 60 * 1000;

/** Delay until the earliest future shift start/end (plus grace), capped as a safety net. */
export function nextOnCallRefreshDelayMs(
  data: { currentOnCall?: { startTime?: string; endTime?: string } | null; nextOnCall?: { startTime?: string; endTime?: string } | null; allSchedules?: Array<{ startTime?: string; endTime?: string }> },
  nextDuty: Array<{ startUtc?: string; endUtc?: string }>,
  nowMs: number,
): number {
  const candidates = [
    data.currentOnCall?.startTime, data.currentOnCall?.endTime,
    data.nextOnCall?.startTime, data.nextOnCall?.endTime,
    ...(data.allSchedules || []).flatMap(entry => [entry?.startTime, entry?.endTime]),
    ...nextDuty.flatMap(entry => [entry.startUtc, entry.endUtc]),
  ];
  let earliest = Number.POSITIVE_INFINITY;
  for (const value of candidates) {
    if (!value) continue;
    const at = parseUtcTimestamp(value).getTime();
    if (Number.isFinite(at) && at > nowMs && at < earliest) earliest = at;
  }
  const delay = Number.isFinite(earliest) ? earliest - nowMs + BOUNDARY_GRACE_MS : MAX_REFRESH_DELAY_MS;
  return Math.min(MAX_REFRESH_DELAY_MS, Math.max(MIN_REFRESH_DELAY_MS, delay));
}

interface OnCallWidgetProps {
  onViewSchedule?: () => void;
  compact?: boolean;
  currentUserId?: string;
  showOnlyMine?: boolean;
  /** Show device presence dots for on-duty operators (Admin/TeamLead; BL-043 UI-060). */
  showPresence?: boolean;
}

type OnCallEntry = {
  scheduleId: string;
  scheduleName: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  escalationUserId?: string;
  escalationUserName?: string;
  escalationUserEmail?: string;
  startTime: string;
  endTime: string;
  source?: string;
  sourceId?: string | null;
};

type OnCallResponse = {
  currentOnCall?: OnCallEntry | null;
  nextOnCall?: OnCallEntry | null;
  allSchedules?: OnCallEntry[];
};

export const OnCallWidget: React.FC<OnCallWidgetProps> = ({ 
  onViewSchedule, 
  compact = false,
  currentUserId,
  showOnlyMine = false,
  showPresence = false,
}) => {
  useDateTimePreferences();
  const [onCallData, setOnCallData] = useState<OnCallResponse | null>(null);
  const [nextDuty, setNextDuty] = useState<Array<{ scheduleName: string; startUtc: string; endUtc: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [presenceByKey, setPresenceByKey] = useState<Map<string, UserPresenceDto>>(new Map());
  const refreshRef = useRef<() => void>(() => undefined);
  useRealtimeRefresh(() => refreshRef.current());

  const presenceScheduleKey = showPresence
    ? Array.from(new Set((onCallData?.allSchedules || []).map(s => String(s?.scheduleId || '').toLowerCase()).filter(Boolean))).sort().join(',')
    : '';

  useEffect(() => {
    if (!presenceScheduleKey) {
      setPresenceByKey(new Map());
      return;
    }
    let active = true;
    const load = async () => {
      try {
        const result = await tenantApi.getOnCallPresence(presenceScheduleKey.split(','));
        if (!active) return;
        const map = new Map<string, UserPresenceDto>();
        for (const schedule of result?.schedules || []) {
          for (const u of schedule.current?.users || []) {
            map.set(`${String(schedule.scheduleId).toLowerCase()}|${String(u.userId).toLowerCase()}`, u.presence);
          }
        }
        setPresenceByKey(map);
      } catch {
        // Presence is supplementary; keep the last known dots on failure.
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), PRESENCE_POLL_MS);
    const onPresenceChanged = () => void load();
    window.addEventListener(ONCALL_PRESENCE_CHANGED_EVENT, onPresenceChanged);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(ONCALL_PRESENCE_CHANGED_EVENT, onPresenceChanged);
    };
  }, [presenceScheduleKey]);

  useEffect(() => {
    let active = true;
    let revision = 0;
    setOnCallData(null);
    setNextDuty([]);
    const refresh = () => {
      const request = ++revision;
      void loadOnCallSchedule(() => active && request === revision);
    };
    refreshRef.current = refresh;
    refresh();

    const onSchedulesChanged = refresh;
    const onFocus = refresh;

    window.addEventListener('nog:schedules:changed', onSchedulesChanged as EventListener);
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      refreshRef.current = () => undefined;
      window.removeEventListener('nog:schedules:changed', onSchedulesChanged as EventListener);
      window.removeEventListener('focus', onFocus);
    };
  }, [currentUserId, showOnlyMine, compact]);

  // Schedule edits arrive via SignalR; handovers are time-driven, so wake up at the next shift boundary.
  useEffect(() => {
    if (!onCallData) return;
    const delay = nextOnCallRefreshDelayMs(onCallData, nextDuty, Date.now());
    const timer = window.setTimeout(() => refreshRef.current(), delay);
    return () => window.clearTimeout(timer);
  }, [onCallData, nextDuty]);

  const normalizeId = (v?: string) => (v || '').trim().replace(/[{}]/g, '').toLowerCase();
  const sameId = (a?: string, b?: string) => normalizeId(a) !== '' && normalizeId(a) === normalizeId(b);

  const loadOnCallSchedule = async (isCurrent: () => boolean) => {
    try {
      setLoading(true);
      let data: OnCallResponse = {};

      try {
        data = await tenantApi.getOnCallSchedule() as OnCallResponse;

      } catch (err) {
        // Fall back to timeline-derived current on-call when API endpoint fails.
        data = {};
      }

      const schedules = await tenantApi.getSchedules();
      const fallbackEntries: OnCallEntry[] = [];
      const mergedBySchedule = new Map<string, OnCallEntry>();

      for (const s of (data?.allSchedules || [])) {
        mergedBySchedule.set(normalizeId(String(s.scheduleId)), s);
      }
      for (const s of fallbackEntries) {
        // Prefer timeline-derived current duty when available
        mergedBySchedule.set(normalizeId(String(s.scheduleId)), s);
      }

      const mergedSchedules = Array.from(mergedBySchedule.values());

      const withPrimaryFirst = mergedSchedules.slice().sort((a, b) => {
        const aEsc = (a.scheduleName || '').toLowerCase().includes('escalation') ? 1 : 0;
        const bEsc = (b.scheduleName || '').toLowerCase().includes('escalation') ? 1 : 0;
        if (aEsc !== bEsc) return aEsc - bEsc;
        return (a.scheduleName || '').localeCompare(b.scheduleName || '', undefined, { sensitivity: 'base' });
      });

      data = {
        ...data,
        allSchedules: withPrimaryFirst,
        currentOnCall: data?.currentOnCall || withPrimaryFirst[0] || null,
      };

      if (showOnlyMine && currentUserId) {
        const normalized = normalizeId(currentUserId);
        const memberScheduleIds = new Set(
          (schedules || [])
            .filter((s: any) => (s.members || []).some((m: any) => sameId(m.userId || m.UserId, currentUserId)))
            .map((s: any) => normalizeId(String(s.scheduleId)))
        );

        const mine = (data?.allSchedules || []).filter((s) =>
          memberScheduleIds.has(normalizeId(String(s.scheduleId))) ||
          sameId(s.userId, currentUserId) ||
          sameId(s.escalationUserId, currentUserId)
        );

        const minePrimaryFirst = mine.slice().sort((a, b) => {
          const aEsc = (a.scheduleName || '').toLowerCase().includes('escalation') ? 1 : 0;
          const bEsc = (b.scheduleName || '').toLowerCase().includes('escalation') ? 1 : 0;
          if (aEsc !== bEsc) return aEsc - bEsc;
          return (a.scheduleName || '').localeCompare(b.scheduleName || '', undefined, { sensitivity: 'base' });
        });

        const preferredCurrent =
          minePrimaryFirst.find((s) => normalizeId(s.userId) === normalized) ||
          minePrimaryFirst[0] ||
          null;

        data = {
          ...data,
          allSchedules: minePrimaryFirst,
          currentOnCall: preferredCurrent,
        };
      } else if (currentUserId) {
        const normalized = normalizeId(currentUserId);
        const allSchedulesMerged = [...(data?.allSchedules || [])];

        const preferredCurrent = allSchedulesMerged.find((s) =>
          normalizeId(s.userId) === normalized
        );

        if (preferredCurrent) {
          data = {
            ...data,
            currentOnCall: preferredCurrent,
          };
        }
      }

      let upcoming: Array<{ scheduleName: string; startUtc: string; endUtc: string }> = [];

      if (currentUserId) {
        const memberSchedules = (schedules || []).filter((s: any) =>
          (s.members || []).some((m: any) => sameId(m.userId || m.UserId, currentUserId))
        );

        // Fall back to all schedules when the membership list doesn't include the
        // current user (they may only appear in a rotation roster, not the
        // schedule-level members array).
        const schedulesToCheck = memberSchedules.length > 0 ? memberSchedules : (schedules || []);

        const now = new Date();
        const end = new Date(now.getTime() + 30 * 86400000);
        const nextRows: Array<{ scheduleName: string; startUtc: string; endUtc: string }> = [];

        await Promise.allSettled(schedulesToCheck.map(async (schedule: any) => {
          const timeline = await tenantApi.getScheduleTimeline(schedule.scheduleId, now.toISOString(), end.toISOString());
          // Get all entries for this user, sorted chronologically.
          const userEntries = (timeline.entries || [])
            .filter((e: any) => sameId(e.userId || e.UserId, currentUserId))
            .sort((a: any, b: any) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime());

          // Find the first entry that is genuinely upcoming — skip any shift
          // that is currently active (startUtc <= now < endUtc).
          const next = userEntries.find((e: any) => new Date(e.startUtc).getTime() > now.getTime());

          if (next) {
            nextRows.push({
              scheduleName: schedule.name,
              startUtc: next.startUtc,
              endUtc: next.endUtc,
            });
          }
        }));

        nextRows.sort((a, b) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime());
        upcoming = nextRows.slice(0, compact ? 2 : 4);
      }
      if (!isCurrent()) return;
      setOnCallData(data);
      setNextDuty(upcoming);
    } catch (err) {
      console.error('Failed to load on-call schedule:', err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // timeline fallback intentionally disabled while backend timeline endpoint is unstable in prod.

  const formatWhen = (iso?: string) => {
    if (!iso) return 'Unknown';
    return formatDateTime(iso, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatName = (entry?: OnCallEntry | null) => {
    if (!entry) return 'Unassigned';
    return entry.userName || entry.userEmail || 'Unassigned';
  };

  const sourceBadge = (source?: string) => {
    const kind = String(source || '').toLowerCase();
    if (kind === 'override') {
      return <span className="on-call-source-badge override" title="A team-wide on-call override is in effect">Override</span>;
    }
    if (kind === 'cover') {
      return <span className="on-call-source-badge cover" title="Covering the rostered operator">Cover</span>;
    }
    return null;
  };

  if (loading && onCallData === null) {
    return (
      <div className="on-call-widget loading" aria-busy="true">
        <h3>On-Call</h3>
        <p>Loading...</p>
      </div>
    );
  }

  const allSchedules = (onCallData?.allSchedules || []).filter(Boolean);
  const isOnDutyNow = !!(currentUserId && allSchedules.some((entry) => sameId(entry.userId, currentUserId)));
  const notificationRows = compact ? allSchedules.slice(0, 3) : allSchedules;

  return (
    <div className="on-call-widget" aria-busy={loading}>
      <div className="on-call-header-row">
        <h3>On-Call</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {!!allSchedules.length && (
            <span className="on-call-count">{allSchedules.length} active</span>
          )}
          {currentUserId && (
            <span className="on-call-count">{isOnDutyNow ? 'On Duty Now' : 'Not On Duty Now'}</span>
          )}
        </div>
      </div>

      {!allSchedules.length && (
        <div className="no-on-call">
          <p>No active on-call windows</p>
        </div>
      )}

      {notificationRows.length > 0 && (
        <div className="on-call-mini-notifications">
          {notificationRows.map((entry) => (
            <div className="on-call-mini-item" key={`${entry.scheduleId}-${entry.userId || entry.userEmail || entry.startTime}`}>
              <span className="mini-dot" aria-hidden="true" />
              <div className="mini-text">
                <div>
                  <strong>{entry.scheduleName}</strong>
                </div>
                <div>
                  On duty: <strong>{formatName(entry)}</strong>
                  {showPresence && entry.userId && (
                    <PresenceDot presence={presenceByKey.get(`${String(entry.scheduleId).toLowerCase()}|${String(entry.userId).toLowerCase()}`) ?? null} />
                  )}
                  {sourceBadge(entry.source)}
                </div>
                <div>
                  Escalation: <strong>{entry.escalationUserName || entry.escalationUserEmail || 'Unassigned'}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {nextDuty.length > 0 && (
        <div className="on-call-mini-notifications">
          <div className="mini-section-title">Your next rostered shifts</div>
          {nextDuty.map((entry) => (
            <div className="on-call-mini-item" key={`${entry.scheduleName}-${entry.startUtc}`}>
              <span className="mini-dot" aria-hidden="true" />
              <div className="mini-text">
                <div>
                  <strong>{entry.scheduleName}</strong>
                </div>
                <div>
                  Starts: <strong>{formatWhen(entry.startUtc)}</strong>
                </div>
                <div>
                  Ends: <strong>{formatWhen(entry.endUtc)}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {currentUserId && nextDuty.length === 0 && (
        <div className="on-call-mini-notifications">
          <div className="mini-section-title">Your next rostered shifts</div>
          <div className="on-call-mini-item">
            <span className="mini-dot" aria-hidden="true" />
            <div className="mini-text">No upcoming shifts in the next 30 days.</div>
          </div>
        </div>
      )}

      {!compact && onViewSchedule && (
        <button className="btn-view-schedule" onClick={onViewSchedule}>
          View Full Schedule
        </button>
      )}
    </div>
  );
};
